import { AddCircleOutline, CheckCircleRounded } from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { usePriceSuggestionTFunction } from '@retail/price-suggestion/i18n';
import { PriceSuggestionType } from '@retail/products/types';
import { getPriceSuggestionType } from '@retail/products/utils';

interface Props {
  priceType: PriceSuggestionType;
  isAdded?: boolean;
  checkPriceType: (priceType: PriceSuggestionType) => void;
  getPriceTypeValue: (priceType: PriceSuggestionType) => string;
}

export function PriceTypeOption({ priceType, isAdded, checkPriceType, getPriceTypeValue }: Props) {
  const t = usePriceSuggestionTFunction();
  return (
    <Stack
      component="button"
      type="button"
      disabled={isAdded}
      onClick={() => checkPriceType(priceType)}
      direction="row"
      alignItems="center"
      gap={1.5}
      sx={(theme) => ({
        appearance: 'none',
        font: 'inherit',
        textAlign: 'left',
        width: '100%',
        px: 1.5,
        py: 1,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'transparent',
        color: 'text.primary',
        cursor: isAdded ? 'default' : 'pointer',
        opacity: isAdded ? 0.6 : 1,
        transition: theme.transitions.create(['border-color', 'background-color']),
        '&:hover': isAdded
          ? undefined
          : {
              borderColor: theme.vars
                ? `rgba(${theme.vars.palette.success.mainChannel} / 0.5)`
                : alpha(theme.palette.success.main, 0.5),
              bgcolor: theme.vars
                ? `rgba(${theme.vars.palette.success.mainChannel} / 0.05)`
                : alpha(theme.palette.success.main, 0.05),
            },
      })}
    >
      <Stack
        flex={1}
        direction="row"
        alignItems="center"
        flexWrap="wrap"
        columnGap={2}
        minWidth={0}
      >
        <Typography variant="body1">{getPriceSuggestionType(t, priceType) || priceType}</Typography>
        {priceType !== 'MARKUP' && (
          <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
            {getPriceTypeValue(priceType)}
          </Typography>
        )}
      </Stack>
      {isAdded ? (
        <CheckCircleRounded fontSize="small" sx={{ color: 'success.main' }} />
      ) : (
        <AddCircleOutline fontSize="small" sx={{ color: 'success.main' }} />
      )}
    </Stack>
  );
}
